import { ComponentProps, memo } from 'react';
import classNames from 'classnames';
import { useTranslation, Trans } from 'react-i18next';
import { isEmpty } from 'lodash';
// Import UI Components
import { Modal, Icon, Button } from '@geneui/components';
// Import Components
import ButtonWithLoader from 'components/common/ButtonWithLoader';
// Import Constants
import { l } from 'constants/common';

interface Props {
    children?: React.ReactNode;
    isVisibleModal: boolean;
    titleText: string;
    onCancel: () => void;
    onOk: () => void;
    isOkActive?: boolean;
    isLoading?: boolean;
    iconType?: string;
    actionLabel: string;
    actionButtonColor?: ComponentProps<typeof Button>['color'];
    cancelLabel?: string;
    className?: string;
    allowDataList?: AnyObject[];
    notAllowDataList?: AnyObject[];
    allowDataLabel?: string;
    notAllowDataLabel?: string;
    questionLabel?: string;
    isBulkAction?: boolean;
    withPortal?: boolean;
}

const ConfirmationModal: React.FC<Props> = (props) => {
    const {
        children,
        isVisibleModal,
        titleText,
        onCancel,
        onOk,
        iconType,
        actionLabel,
        actionButtonColor = 'primary',
        cancelLabel = l.Cancel,
        className,
        isOkActive = true,
        isLoading,
        allowDataList,
        notAllowDataList,
        allowDataLabel,
        notAllowDataLabel,
        questionLabel,
        isBulkAction,
        withPortal,
    } = props;
    const { t } = useTranslation();

    const { selectedCount, selectedAllowed, selectedNotAllowed } = {
        selectedCount: (allowDataList?.length ?? 0) + (notAllowDataList?.length ?? 0),
        selectedAllowed: allowDataList,
        selectedNotAllowed: notAllowDataList,
    };

    const getLabel = () => {
        if (isEmpty(allowDataList)) {
            return t(notAllowDataLabel ?? '');
        }
        if (isEmpty(notAllowDataList)) {
            return t(allowDataLabel ?? '', {
                selectedCount: allowDataList?.length,
            });
        }
        return questionLabel;
    };

    return (
        <Modal
            background="dark-background"
            position="top"
            title={titleText}
            size="content-size"
            className={classNames('crm-modal', className)}
            appearance="compact"
            closable={false}
            closeOnClickOutside={true}
            visible={isVisibleModal}
            isOkActive={isOkActive}
            withPortal={withPortal}
            footer={
                <>
                    <Button color="default" appearance="minimal" onClick={onCancel}>
                        {t(cancelLabel)}
                    </Button>
                    <ButtonWithLoader
                        color={actionButtonColor}
                        onClick={onOk}
                        isLoading={isLoading}
                        isDisabled={!isOkActive}
                    >
                        {actionLabel}
                    </ButtonWithLoader>
                </>
            }
        >
            <>
                {iconType && (
                    <div className="crm-circle-icon-cnt">
                        <Icon type={iconType} />
                    </div>
                )}

                <div className="crm-allowd-not-allowd-cnt">
                    <Trans
                        defaults={isBulkAction ? getLabel() : questionLabel}
                        values={{ selectedCount }}
                        components={{
                            selectedAllowed: (
                                <ul>
                                    {selectedAllowed?.map((item, index) => (
                                        <li key={index}>{item.name}</li>
                                    ))}
                                </ul>
                            ),
                            selectedNotAllowed: (
                                <ul>
                                    {selectedNotAllowed?.map((item, index) => (
                                        <li key={index}>{item.name}</li>
                                    ))}
                                </ul>
                            ),
                        }}
                    />
                </div>

                {children}
            </>
        </Modal>
    );
};

export default memo(ConfirmationModal);
